import { useEffect, useRef, useState } from 'react'
import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
} from 'react'

/**
 * A gutter an operator can drag, reach from the keyboard, and get back.
 *
 * The console already had this, twice, and neither copy is one a third screen
 * should have inherited. `useResizableColumns` in fleet-telemetry drives three
 * gutters across Oturumlar and Canlı Operasyon and carries two defects that
 * ADR-0027 records rather than propagates:
 *
 *   *It binds `pointerup` and not `pointercancel`.* wall.tsx says what that
 *   costs, in a comment written after somebody met it: a cancelled pointer — a
 *   browser gesture taking over, a stylus leaving the digitiser, a device
 *   disconnecting — never sends `pointerup`, so the window `pointermove`
 *   listener stays bound and `document.body` keeps `user-select: none` and a
 *   resize cursor until the page is reloaded. Every drag here ends through one
 *   function, and all three pointer events reach it.
 *
 *   *It writes localStorage from an effect on the value*, which means once per
 *   `pointermove` — a synchronous serialise on the main thread, sixty times a
 *   second, for the whole of a drag. A drag here writes once, when the gesture
 *   ends, and only if it moved anything. A *held arrow key* still writes per
 *   auto-repeat, and that is not fixed: a keyboard gesture has no `pointerup`
 *   to land on, so coalescing it needs a trailing timer flushed on unmount and
 *   on blur, and a flush that is missed loses the size instead of merely
 *   writing it too often. Recorded rather than papered over — a repeat burst is
 *   a burst, not the whole of a gesture.
 *
 * And both copies are mouse-only. `LiveOpsPage` renders `role="separator"` with
 * no `tabIndex`, no key handler and no `aria-value*`: a separator that cannot be
 * focused is a line, not a control, and a pane whose only size control is a 6px
 * drag target has no keyboard path at all. wall.tsx's span handle is the
 * in-repo precedent that does it right — arrows, on the element, each one
 * preventing the page's own scroll — and `bind` is that, generalised.
 *
 * **Why this is not `useEditable`.** The invariant that a prefill never
 * overrules a human applies to a field seeded from the server, where a refetch
 * or a socket delta can arrive later and overwrite what somebody typed. There is
 * no server value here and nothing to refetch: the only writer of `value` is the
 * operator, through a drag, a key or a reset, and the stored number is read
 * exactly once, in the `useState` initialiser. The invariant is honoured by
 * construction, and wrapping a purely local number in `useEditable` would claim
 * a server source that does not exist. What is *not* acceptable, and is the
 * failure this note exists to prevent, is seeding with
 * `useEffect(() => setValue(stored), [stored])` — that effect is the shape of
 * the defect, and it would run again on every render that changed the key.
 *
 * The generic `S` is whatever the caller measures at pointerdown — a starting
 * width, a container's box, a row height. It is captured once and every move is
 * arithmetic on it, so a drag cannot compound its own rounding, and a container
 * that reflows under the pointer cannot make the gutter run away.
 */
export interface SplitterSpec<S> {
  /** Where the chosen number lives across reloads. */
  storageKey: string
  /** The axis the gutter travels along; `'x'` is a vertical separator. */
  axis: 'x' | 'y'
  /** The size before anybody has an opinion, and where `reset` goes back to. */
  initial: number
  min: number
  max: number
  /** One arrow press, in the units of `value`. */
  step: number
  /** One arrow press with Shift held. Defaults to four steps. */
  bigStep?: number
  /** Already translated: this hook never reaches for `useT`. */
  label: string
  /** The size said in words, for a screen reader. */
  valueText: (v: number) => string
  /** Measured once, at pointerdown, and handed back to every `toValue`. */
  origin: (gutter: HTMLElement, current: number) => S
  /** The size this pointer position means, before clamping. */
  toValue: (o: S, dx: number, dy: number) => number
}

export interface Splitter {
  value: number
  dragging: boolean
  set: (v: number) => void
  reset: () => void
  /** Spread onto the gutter element; it carries the ARIA as well as the events. */
  bind: Record<string, unknown>
}

export function clampTo(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v))
}

/**
 * A stored size, or the fallback — never an exception and never `NaN`.
 *
 * Keeps the posture `readWidths` in fleet-telemetry states and the reason it
 * gives: a preference written by a person, by a different version, or by a write
 * that did not finish is not an error to report, it is a preference to ignore.
 * The type check is deliberate on top of that — `JSON.parse('[]')` throws
 * nothing and `Number([])` is `0`, which would clamp to `min` and look for all
 * the world like a size somebody chose.
 */
export function readNumber(key: string, fallback: number, min: number, max: number): number {
  try {
    const raw = localStorage.getItem(key)
    if (raw !== null) {
      const stored: unknown = JSON.parse(raw)
      if (typeof stored === 'number' && Number.isFinite(stored)) return clampTo(stored, min, max)
    }
  } catch {
    // Both the read and the parse are in here: localStorage itself throws when
    // storage is disabled, and that is not a reason for a pane to fail to draw.
  }
  return clampTo(fallback, min, max)
}

function writeNumber(key: string, v: number): void {
  try {
    localStorage.setItem(key, JSON.stringify(v))
  } catch {
    // Full, or disabled. A preference that cannot be saved is not worth failing
    // the gesture that produced it.
  }
}

/** Probe distance for `gutterSign`, in pixels — see there. */
const SIGN_PROBE = 16

/**
 * Which way the number runs when the gutter moves forward along its axis.
 *
 * The arrows have to move the *separator* the way they point — that is most of
 * why a keyboard path is worth having — but the number underneath does not
 * always run with them. A bottom pane sized by a cap grows as the pointer moves
 * *up*, so its `toValue` subtracts `dy`, and a flat `+step` on ArrowDown would
 * send the gutter the opposite way from the key.
 *
 * The caller has already said which it is, in `toValue`. Asking it what a
 * pixel's travel does is the same measurement a drag makes at pointerdown, so
 * the keys cannot disagree with the pointer. The rejected alternative is a
 * `reversed` flag on the spec: a second statement of a fact the spec already
 * carries, and therefore one somebody can set to contradict the other.
 *
 * Probed symmetrically and over 16px rather than one, because a `toValue` that
 * snaps to a grid can answer the same number for a single pixel either side of
 * where it is now; forward when the two are equal, which is where a splitter
 * with no opinion should go.
 */
function gutterSign<S>(spec: SplitterSpec<S>, gutter: HTMLElement, current: number): 1 | -1 {
  const o = spec.origin(gutter, current)
  const along = spec.axis === 'x'
  const ahead = spec.toValue(o, along ? SIGN_PROBE : 0, along ? 0 : SIGN_PROBE)
  const behind = spec.toValue(o, along ? -SIGN_PROBE : 0, along ? 0 : -SIGN_PROBE)
  if (!Number.isFinite(ahead) || !Number.isFinite(behind)) return 1
  return ahead < behind ? -1 : 1
}

export function useSplitter<S>(spec: SplitterSpec<S>): Splitter {
  const { storageKey, axis, initial, min, max, step, label, valueText } = spec
  const bigStep = spec.bigStep ?? step * 4

  // A `useState` initialiser, which runs once. Never an effect that assigns the
  // stored value after the first render: that effect is the shape of the prefill
  // defect, and the limits it would be keyed on are measured — `max` is usually
  // the container's width — so it would re-run on every window resize and throw
  // away the drag that had just happened.
  const [chosen, setChosen] = useState(() => readNumber(storageKey, initial, min, max))
  const [dragging, setDragging] = useState(false)

  // What the layout can honour today, which is not always what the operator
  // chose: `max` is usually measured, so narrowing the window can put a stored
  // size out of range. Clamped on *read*, so the number in state stays the
  // operator's — widen the window again and the pane comes back. The rejected
  // alternative is an effect keyed on the limits that assigns the clamped value:
  // it is the prefill defect's shape, and it would also write the shrunken size
  // to storage, which is how a preference is lost by resizing a window. Without
  // this the gutter reports `aria-valuenow` above its own `aria-valuemax` and
  // the pane draws wider than the track it is in.
  const value = clampTo(chosen, min, max)

  // The window listeners a drag installs outlive the render that installed them,
  // so they must not read `value`: at pointerup the closure still holds the
  // number from pointerdown, and that is the number that would be stored. `put`
  // writes the ref in the same call as the state, so within a gesture it is
  // never behind, and pointerdown re-seeds it — between gestures the limits may
  // have moved, and the ref then holds what was chosen rather than what is
  // drawn.
  const valueRef = useRef(value)

  // Teardown for the drag in flight, or null. Kept so unmount can run it: the
  // listeners are on the window and the styles are on the body, and neither goes
  // away with the element that started them.
  const endRef = useRef<(() => void) | null>(null)

  const put = (next: number): number => {
    // `clampTo(NaN, …)` is `NaN`, and `NaN` reaches the layout as a pane that
    // collapses and stays collapsed. The drag path already refuses one before
    // getting here; the check belongs at the choke point too, because `set` is
    // public and a caller sizing a pane off a container with no box yet is the
    // same measurement failing one layer further out.
    if (!Number.isFinite(next)) return valueRef.current
    const v = clampTo(next, min, max)
    valueRef.current = v
    setChosen(v)
    return v
  }

  const set = (next: number) => writeNumber(storageKey, put(next))

  const reset = () => {
    put(initial)
    try {
      // Removed rather than written back as `initial`: a reset says "I have no
      // preference here", and storing today's default would freeze it into that
      // browser — a later change to the layout's own default would then never
      // reach anybody who had ever reset a gutter.
      localStorage.removeItem(storageKey)
    } catch {
      // Same as writeNumber, and the same reason.
    }
  }

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    // The gutter is also somewhere to hang buttons — collapse a pane, swap the
    // two — and a press on one of those is a click on that button, not the start
    // of a drag. Anything nested reports itself as the target, so the identity
    // check is the whole rule.
    if (e.target !== e.currentTarget) return
    // The main button of the primary pointer, and nothing else. A right-press
    // raises the context menu and on several platforms never delivers the
    // `pointerup` that would undo `user-select: none` — the same page-wide leak
    // `pointercancel` is bound for, arriving through the one door that was left
    // open. And a second finger pressing the gutter part-way through a drag
    // would otherwise take the gesture over from the first — a resize nobody
    // asked for. Refused, because two pointers on one separator have no agreed
    // meaning.
    if (e.button !== 0 || !e.isPrimary) return
    // A drag still in flight here means its ending never arrived. Tear it down
    // before installing another, or both sets of listeners drive the same
    // gutter and the one that is stale wins every other frame.
    endRef.current?.()
    e.preventDefault()

    // Seeded from the derived value, not from whatever the last gesture left in
    // the ref: the limits may have moved since, and a drag has to start from the
    // size that is on the screen.
    valueRef.current = value
    const o = spec.origin(e.currentTarget, value)
    // The gesture belongs to one pointer, and every listener below checks that
    // it is hearing from that one. `pointermove` on the window is *every*
    // pointer: a second thumb resting anywhere on the screen, a pen hovering
    // over a tablet. Without the check that pointer's coordinates are measured
    // against this gesture's origin, and the pane jumps to wherever it is —
    // which is not a corner case on a console that ships to a phone. The
    // rejected alternative is `setPointerCapture`: it retargets the events but
    // does not stop the others arriving at the window, so the check is needed
    // either way and the capture would only add a state to lose.
    const id = e.pointerId
    const startX = e.clientX
    const startY = e.clientY
    const before = value

    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== id) return
      const next = spec.toValue(o, ev.clientX - startX, ev.clientY - startY)
      // A non-finite answer means the origin measured something that was not
      // there — a container with no box yet, an element unmounted under the
      // pointer. Keeping the last good number is harmless; `NaN` reaches the
      // layout as a collapsed pane and stays collapsed after the drag ends.
      if (!Number.isFinite(next)) return
      put(next)
    }

    const end = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', finish)
      window.removeEventListener('pointercancel', finish)
      window.removeEventListener('keydown', onEscape)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
      endRef.current = null
      setDragging(false)
    }

    // One function for pointerup and pointercancel both, and the write is here
    // rather than in an effect on the value: once for the gesture instead of
    // once for every frame of it.
    const finish = (ev: PointerEvent) => {
      if (ev.pointerId !== id) return
      end()
      // Only if the gesture actually moved it. A press that goes nowhere — a
      // click on the gutter, or the two presses inside a double-click — would
      // otherwise write today's `initial` into storage, which is the exact thing
      // `reset` removes the key to avoid: a default frozen into one browser, by
      // a stray click, on a gutter nobody has ever dragged.
      if (valueRef.current !== before) writeNumber(storageKey, valueRef.current)
    }

    const onEscape = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return
      ev.preventDefault()
      put(before)
      // Deliberately `end` and not `finish`: the value is back to what storage
      // already holds, and a write here would record a size the operator has
      // just abandoned — on a gutter they may never have moved before.
      end()
    }

    // On the window, not on the element: the pointer leaves a 6px gutter almost
    // at once, and a listener on the gutter would end the drag there. The Escape
    // watcher is on the window for a different reason — a pointer press does not
    // reliably focus what it pressed, so the gutter's own `onKeyDown` may never
    // see the key that is supposed to call the drag off.
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', finish)
    window.addEventListener('pointercancel', finish)
    window.addEventListener('keydown', onEscape)
    document.body.style.userSelect = 'none'
    document.body.style.cursor = axis === 'x' ? 'col-resize' : 'row-resize'
    endRef.current = end
    setDragging(true)
  }

  const onKeyDown = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      reset()
      return
    }
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault()
      set(e.key === 'Home' ? min : max)
      return
    }
    const forward = axis === 'x' ? 'ArrowRight' : 'ArrowDown'
    const back = axis === 'x' ? 'ArrowLeft' : 'ArrowUp'
    // Across the axis, nothing at all — not even a preventDefault. An ArrowUp on
    // a vertical separator belongs to whatever scrolls the page, and swallowing
    // it would make the gutter a trap for the keys it has no answer for.
    if (e.key !== forward && e.key !== back) return
    e.preventDefault()
    const travel = (e.shiftKey ? bigStep : step) * (e.key === forward ? 1 : -1)
    set(value + travel * gutterSign(spec, e.currentTarget, value))
  }

  const onDoubleClick = (e: ReactMouseEvent<HTMLElement>) => {
    // Same reason as pointerdown: a double-click that lands on a button hung in
    // the gutter is that button's, twice, and must not resize anything.
    if (e.target !== e.currentTarget) return
    reset()
  }

  // Unmounting mid-drag must not leave the page unselectable either. A route
  // change under the pointer, or a pane closing beneath it, ends the drag the
  // same way a pointercancel does.
  useEffect(() => () => endRef.current?.(), [])

  return {
    value,
    dragging,
    set,
    reset,
    bind: {
      role: 'separator',
      // Without this the separator is a line, not a control: it cannot be
      // tabbed to, so `onKeyDown` can never fire and the pane has no size
      // control at all for anyone not holding a pointer.
      tabIndex: 0,
      // `'x'` is a gutter that travels left and right, which makes the separator
      // itself vertical: it separates left from right.
      'aria-orientation': axis === 'x' ? 'vertical' : 'horizontal',
      'aria-label': label,
      // Not rounded, and this was a real defect: the vertical split on Dosyalar
      // is stored as a *ratio* (Files.css says why — two peers must give ground
      // together), so `Math.round` turned 0.5 between 0.2 and 0.8 into a
      // separator announcing itself at its maximum while sitting in the middle.
      // Rounding bought nothing anyway: `valueText` is required precisely so
      // the spoken form is the caller's, and a screen reader prefers
      // `aria-valuetext` wherever it is present, which here is everywhere.
      'aria-valuenow': value,
      'aria-valuemin': min,
      'aria-valuemax': max,
      'aria-valuetext': valueText(value),
      onPointerDown,
      onKeyDown,
      onDoubleClick,
    },
  }
}
